import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { Share } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";
import { afTrack } from "../analytics/appsflyer";

export default function SharePage({ userInfo }) {
  const [status, setStatus] = useState("");
  const [referralStats, setReferralStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const referralCode =
    referralStats?.ref_code ||
    userInfo?.ref_code ||
    userInfo?.referral_code ||
    "";

  const shareUrl = referralCode
    ? `https://satstacker.app/download?ref=${encodeURIComponent(referralCode)}`
    : "";

  const shareText =
    "I use SatStacker for my Bitcoin DCA ⚡\n" +
    "Use my link and you'll get 2 months of Smart Timing free instead of the usual 1 month.\n" +
    "Same budget. Same cadence. Smarter entries.";

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const res = await api.get(
          "/me/referrals"
        );

        if (!cancelled) {
          setReferralStats(res.data || null);
        }
      } catch (err) {
        console.warn("Referral stats unavailable", err);
      } finally {
        if (!cancelled) setLoadingStats(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, []);

  const onShare = async () => {

    if (!referralCode) {
      setStatus("Your referral link is not available yet. Please try again.");
      return;
    }

    afTrack("referral_share_tapped", {
      entry: "share_page",
      qualified_referrals:
        referralStats?.qualified_referrals ?? null,
    });

    try {
      if (Capacitor.isNativePlatform()) {
        await Share.share({
          title: "Get 2 months of Smart Timing free",
          text: shareText,
          url: shareUrl,
        });

        setStatus("Referral link shared.");
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: "Get 2 months of Smart Timing free",
          text: shareText,
          url: shareUrl,
        });

        setStatus("Referral link shared.");
        return;
      }

      await navigator.clipboard.writeText(
        `${shareText}\n\n${shareUrl}`
      );

      setStatus("Referral link copied.");
    } catch (e) {
      const msg = String(e?.message || "").toLowerCase();

      if (
        msg.includes("cancel") ||
        msg.includes("abort")
      ) {
        return;
      }

      setStatus(
        "Couldn’t open sharing. You can copy your link below."
      );
    }
  };

  const onCopy = async () => {
    if (!referralCode) {
      setStatus("Your referral link is not available yet. Please try again.");
      return;
    }

    try {
      await navigator.clipboard.writeText(
        `${shareText}\n\n${shareUrl}`
      );

      afTrack("referral_link_copied", {
        entry: "share_page",
      });

      setStatus("Referral link copied.");
    } catch {
      setStatus(
        "Copy failed — select and copy the link below."
      );
    }
  };

  const qualified =
    referralStats?.qualified_referrals ?? 0;

  const monthsEarned =
    referralStats?.months_earned ??
    qualified;

  return (
    <div className="min-h-screen bg-zinc-950 text-white px-4 py-6">
      <div className="max-w-lg mx-auto flex flex-col gap-5">

        {/* HERO */}
        <section className="rounded-2xl border border-orange-500/25 bg-orange-500/[0.04] p-6 text-center">
          <div className="inline-flex items-center rounded-full border border-orange-500/20 bg-orange-500/10 px-3 py-1.5 text-xs font-bold text-orange-400">
            🎁 SatStacker Referral
          </div>

          <h1 className="mt-4 text-3xl font-black leading-tight">
            Give a month.
            <span className="block text-orange-400">
              Get a month.
            </span>
          </h1>

          <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
            Friends you invite get{" "}
            <span className="font-bold text-white">
              2 months of Smart Timing free
            </span>{" "}
            instead of the usual 1 month.
          </p>

          <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
            When they start Smart Timing, you earn an
            extra free month too.
          </p>

          <button
            type="button"
            onClick={onShare}
            className="mt-6 w-full py-3.5 rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-black transition"
          >
            Give a friend 2 months free →
          </button>

          <p className="mt-2 text-[11px] text-zinc-500">
            1 qualified friend = 1 free month earned
          </p>
        </section>

        {/* PROGRESS */}
        <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
          <p className="text-xs uppercase tracking-[0.12em] font-bold text-zinc-500">
            Your referrals
          </p>

          {loadingStats ? (
            <div className="mt-4 text-sm text-zinc-500 animate-pulse">
              Loading referral progress…
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 text-center">
                <div className="text-2xl font-black text-white">
                  {qualified}
                </div>
                <div className="mt-1 text-xs text-zinc-500">
                  friends started Smart Timing
                </div>
              </div>

              <div className="rounded-xl border border-orange-500/20 bg-orange-500/5 p-4 text-center">
                <div className="text-2xl font-black text-orange-400">
                  {monthsEarned}
                </div>
                <div className="mt-1 text-xs text-zinc-500">
                  free months earned
                </div>
              </div>
            </div>
          )}

          {!loadingStats &&
            (referralStats?.referred_signups ?? 0) >
              qualified && (
              <p className="mt-3 text-xs text-zinc-500 text-center">
                {Math.max(
                  0,
                  (referralStats?.referred_signups ?? 0) -
                    qualified
                )}{" "}
                referral
                {Math.max(
                  0,
                  (referralStats?.referred_signups ?? 0) -
                    qualified
                ) === 1
                  ? ""
                  : "s"}{" "}
                signed up but haven’t started Smart Timing yet.
              </p>
            )}
        </section>

        {/* HOW IT WORKS */}
        <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
          <p className="text-xs uppercase tracking-[0.12em] font-bold text-zinc-500">
            How it works
          </p>

          <div className="mt-4 space-y-4">
            <div className="flex gap-3">
              <div className="h-8 w-8 shrink-0 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 flex items-center justify-center font-black text-sm">
                1
              </div>

              <div>
                <p className="text-sm font-bold text-white">
                  Share your referral link
                </p>
                <p className="mt-1 text-xs text-zinc-500">
                  Send it to another Bitcoiner who wants to
                  automate their DCA.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="h-8 w-8 shrink-0 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 flex items-center justify-center font-black text-sm">
                2
              </div>

              <div>
                <p className="text-sm font-bold text-white">
                  They get 2 months free
                </p>
                <p className="mt-1 text-xs text-zinc-500">
                  Referral users get an extra free month of
                  Smart Timing.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="h-8 w-8 shrink-0 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 flex items-center justify-center font-black text-sm">
                3
              </div>

              <div>
                <p className="text-sm font-bold text-white">
                  You earn a free month
                </p>
                <p className="mt-1 text-xs text-zinc-500">
                  Your reward is earned when your friend starts
                  Smart Timing.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* COPY LINK */}
        <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
          <p className="text-sm font-semibold text-white">
            Your referral link
          </p>

          <div className="mt-3 break-all rounded-xl border border-zinc-800 bg-zinc-950/60 px-3 py-3 font-mono text-xs text-zinc-400">
            {shareUrl}
          </div>

          <button
            type="button"
            onClick={onCopy}
            className="mt-3 w-full py-3 rounded-xl border border-zinc-700 bg-zinc-950 hover:bg-zinc-800 text-white font-semibold transition"
          >
            Copy referral link
          </button>

          {status && (
            <p className="mt-3 text-xs text-zinc-400 text-center">
              {status}
            </p>
          )}
        </section>

        <p className="text-[10px] text-zinc-600 text-center pb-6 leading-relaxed">
          Referral rewards apply to qualifying new Smart Timing
          customers. Subscription terms apply.
        </p>
      </div>
    </div>
  );
}